import {
	AnswerEvent, ContextShownEvent, FinishEvent, FlipCardEvent,	NavigateEvent,
	NextEvent, PauseEvent, PreviousEvent, ReloadEvent, ToggleContextsEvent,
	ToggleShortcutModalEvent, ToggleTimerEvent
} from "../../events";
import { ItemId, Serializer } from "../../model/datasource";
import { BindRecord, KeyBindRecord } from "./frontend";
import { ContentItem, ItemType } from "../../model/datasource/item";
import {
	DisplayItemFrontend, FlashcardFrontend,
	MultipleChoiceFrontend, MultipleCorrectFrontend, NumericFrontend,
	ClozeFrontend, MatchingFrontend, SortingFrontend,
	ConstructedResponseFrontend, UnknownFrontend
} from "./displayitem";
import { MultipleChoiceAnswer, MultipleCorrectAnswer } from "../../lib/userdata";
import Sortable from "./displayitem/sortable";
import { Swap } from "sortablejs";
import { SwipeLeftEvent, SwipeRightEvent } from "../../lib/swipe";

export abstract class ItemFrontend<I extends ItemId> {
	itemIdSerializer: Serializer<I>;
	constructor(item: Serializer<I>) {
		this.itemIdSerializer = item;
	}

	keyboardListener: (e: KeyboardEvent) => void;

	getSwipeTarget(): NodeListOf<HTMLElement> {
		return document.querySelectorAll('div.item');
	}

	getBindHashChange(): BindRecord {
		return new BindRecord(
			[window],
			'hashchange',
			element => new NavigateEvent(decodeURIComponent(location.hash.replace(/^#/, '')))
		)
	}

	getBindNext(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.next'),
			'click',
			element => new NextEvent()
		);
	}

	getBindSwipeLeft(): BindRecord {
		return new BindRecord(
			this.getSwipeTarget(),
			SwipeLeftEvent.type,
			element => new NextEvent()
		)
	}

	getKeyBindNext(): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type) => key === 'ArrowRight',
			(answers, type) => new NextEvent()
		);
	}

	getBindPrevious(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.prev'),
			'click',
			element => new PreviousEvent()
		);
	}

	getBindSwipeRight(): BindRecord {
		return new BindRecord(
			this.getSwipeTarget(),
			SwipeRightEvent.type,
			element => new PreviousEvent()
		)
	}

	getKeyBindPrevious(): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type) => key === 'ArrowLeft',
			(answers, type) => new PreviousEvent()
		);
	}

	getBindReload(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.reload'),
			'click',
			element => new ReloadEvent()
		);
	}

	getBindToggleContext(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.context-panel'),
			'click',
			(element: HTMLElement) => new ToggleContextsEvent([element.dataset.context])
		);
	}

	getKeyBindToggleContext(): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type) => key === '`',
			(answers, type) => new ToggleContextsEvent(
				Array.from(document.querySelectorAll('.context-panel'))
					.map((e: HTMLElement) => e.dataset.context)
			)
		);
	}

	getBindFinish(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.finish'),
			'click',
			element => new FinishEvent()
		);
	}

	getKeyBindAnswer(index: number): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type: ItemType) =>
				key === [
					'a', 'b', 'c', 'd', 'e',
					'f', 'g', 'h', 'i', 'j'
				][index] &&
				answers.length > index,
			(answers, type: ItemType) => {
				const value = answers[index].value;
				if (type === 'multiplechoice') {
					return new AnswerEvent(value);
				} else if (type === 'multiplecorrect') {
					const answerSet = new Set(Array.from(answers)
						.filter(answer => answer.checked)
						.map(answer => answer.value)
					);
					if (answerSet.has(value)) {
						answerSet.delete(value);
					} else {
						answerSet.add(value);
					}
					return new AnswerEvent(Array.from(answerSet).sort());
				}
			}
		);
	}

	getBindContextShown(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.context-panel'),
			ContextShownEvent.type,
			(element, event: ContextShownEvent) => new ContextShownEvent(event.detail)
		);
	}

	getBindResume(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.pause-test .resume'),
			'click',
			element => new PauseEvent(false)
		);
	}

	getBindHideTimer(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.pause-test .hide'),
			'click',
			element => [new PauseEvent(false), new ToggleTimerEvent()]
		);
	}

	getKeyBindShortcutModal(): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type) => key === '?',
			(answers, type) => new ToggleShortcutModalEvent()
		);
	}

	getAnswers(): NodeListOf<HTMLInputElement> {
		return document.querySelectorAll<HTMLInputElement>(
			'.item > :not(.d-none) .answers .answer input'
		);
	}

	bindKeyboard(bindings: KeyBindRecord[], answers: NodeListOf<HTMLInputElement>, type: ItemType, bindTarget: EventTarget, isKeyboardShortcuts: () => boolean): void {
		if (this.keyboardListener) {
			document.removeEventListener('keydown', this.keyboardListener);
		}
		this.keyboardListener = (e: KeyboardEvent) => {
			// TODO: Figure out a different way to catch top level key events
			if (!['BODY', '#document'].includes((e.target as Node).nodeName)) {
				if (e.key === 'Escape') { (e.target as HTMLElement).blur(); }
			} else if (isKeyboardShortcuts()) {
				bindings.forEach(bind => {
					if (bind.condition(e.key, answers, type)) {
						if (bind.suppress) { e.preventDefault(); }
						const event = bind.invoke(answers, type);
						bindTarget.dispatchEvent(event);
					}
				});
			}
		};
		document.addEventListener('keydown', this.keyboardListener);
	}

	bindJquery(jQuery: JQueryStatic, selector: string, event: string): void {
		jQuery(selector).on(event, e => {
			e.target.dispatchEvent(new CustomEvent(
				event,
				{ detail: e }
			));
		});
	}

	setVisible(element: Element, state: boolean): void {
		const int = Number, cls = ['d-none', 'd-flex'];
		if (element === document.querySelector('div.item')) {
			element.classList.toggle('d-none', !state);
		} else {
			element.classList.replace(cls[int(!state)], cls[int(state)]);
		}
	}

	displayLoading(status: "done" | "loading" | "error"): void {
		Object.entries({
			done: document.querySelector('div.item'),
			loading: document.querySelector('div.item-loading'),
			error: document.querySelector('div.item-errored'),
		}).forEach(([state, element]) => this.setVisible(element, state == status));
	}

	displayFinishing(status: "ready" | "loading" | "error"): void {
		const button = document.querySelector('.modal.finish-test .finish');
		const loading = document.querySelector('.modal.finish-test .loading');
		const error = document.querySelector('.modal.finish-test .error');
		button.classList.toggle('d-none', status !== 'ready' && status !== 'error');
		loading.classList.toggle('d-none', status !== 'loading');
		error.classList.toggle('d-none', status !== 'error');
	}

	displayUnknown(): void {
		const unknown = document.querySelector('.item .unknown');
		unknown.classList.remove('d-none');
	}

	toggleContexts(ids: string[]): void {
		this.displayContexts(Array.from(document.querySelectorAll(
			'.item > :not(.d-none) .context-panel'
		)).reduce((o: Record<string, boolean>, e: HTMLElement) => {
			const collapse = e.querySelector('.context-collapse');
			return Object.assign(o, {
				[e.dataset.context]: ids.includes(e.dataset.context)
					? !collapse.classList.contains('show')
					: collapse.classList.contains('show')
			});
		}, {}));
	}

	/**
	 * Function will set given contexts to expanded or collapsed according to
	 * their shown attribute in the contexts record. Any contexts not included
	 * in the record will default to being shown.
	 *
	 * @param {Record<string, boolean>} contexts A record of context IDs to
	 * boolean shown flags.
	 */
	displayContexts(contexts: Record<string, boolean>): void {
		const top_panel = document.querySelector(
			'.item > :not(.d-none) .context-panel'
		);
		Array.from(document.querySelectorAll<HTMLElement>(
			'.item > :not(.d-none) .context-panel'
		)).forEach((panel: HTMLElement) => {
			const show = contexts[panel.dataset.context] ?? true;
			const collapse = panel.querySelector<HTMLElement>('.context-collapse');
			const gradient = panel.querySelector('.context-gradient');
			if (show) {
				collapse.classList.add('show');
				collapse.style.maxHeight = 'max-content';
				gradient.classList.replace('d-flex', 'd-none');
				panel.querySelectorAll('.context-collapser i')
					.forEach(e => e.classList.replace(
						'fa-chevron-down',
						'fa-chevron-up'
					));
				top_panel.dispatchEvent(
					new ContextShownEvent(panel.dataset.context)
				);
			} else {
				collapse.classList.remove('show');
				collapse.style.maxHeight = '9em';
				gradient.classList.replace('d-none', 'd-flex');
				panel.querySelectorAll('.context-collapser i')
					.forEach(e => e.classList.replace(
						'fa-chevron-up',
						'fa-chevron-down'
					));
			}
		});
	}

	displayFontSize(fontSize: number): void {
		document.querySelectorAll<HTMLElement>('.viewer .item')
			.forEach(item => {
				item.style.fontSize = `${fontSize}em`;
			});
	}

	displayResults(url: string): void {
		const parsed = new URL(url, window.location.origin);
		window.location.href = parsed.toString();
		const redirect = document.querySelector('.modal.finish-test .redirect');
		redirect.classList.remove('d-none');
		const anchor: HTMLAnchorElement = redirect.querySelector('a.url');
		anchor.href = parsed.toString();
	}

	displayPauseModal(jQuery: JQueryStatic, command: "toggle" | "show" | "hide"): void {
		jQuery('.pause-test').modal(command);
	}

	abstract displayItemClass(content: ContentItem<I>): DisplayItemFrontend<I>;
}

export class PqItemFrontend<I extends ItemId> extends ItemFrontend<I> {
	displaySelectedAnswerMultipleChoice(answer: MultipleChoiceAnswer): void {
		document.querySelectorAll('.multiplechoice .answers .answer')
			.forEach(other => {
				const label = other.querySelector('label.border');
				const input = other.querySelector('input');
				if (label) {
					label.classList.remove('border', 'rounded', 'bg-light');
				}
				input.checked = false;
			});
		const input = document.querySelector<HTMLInputElement>(
			`.multiplechoice .answers .answer input[value="${answer}"]`
		);
		if (input) {
			const element = input.closest('.answer');
			const label = element.querySelector('label');
			label.classList.add('border', 'rounded', 'bg-light');
			input.checked = true;
		}
	}

	displaySelectedAnswerMultipleCorrect(answer: MultipleCorrectAnswer): void {
		const answerSet = new Set(answer);
		document.querySelectorAll('.multiplecorrect .answers .answer')
			.forEach(other => {
				const label = other.querySelector('label');
				const input = other.querySelector('input');
				if (answerSet.has(input.value)) {
					label.classList.add('border', 'rounded', 'bg-light');
					input.checked = true;
				} else {
					if (label) {
						label.classList.remove('border', 'rounded', 'bg-light');
					}
					input.checked = false;
				}
			});
	}

	displayItemClass(content: ContentItem<I>): DisplayItemFrontend<I> {
		if (content.type === 'multiplechoice') {
			return new MultipleChoiceFrontend();
		} else if (content.type === 'multiplecorrect') {
			return new MultipleCorrectFrontend();
		} else if (content.type === 'numeric') {
			return new NumericFrontend();
		} else if (content.type === 'cloze') {
			return new ClozeFrontend();
		} else if (content.type === 'matching') {
			return new MatchingFrontend();
		} else if (content.type === 'sorting') {
			return new SortingFrontend();
		} else if (content.type === 'constructedresponse') {
			return new ConstructedResponseFrontend();
		} else {
			return new UnknownFrontend();
		}
	}

	initSortable(): void {
		Sortable.mount(new Swap());
	}
}

export class FlashItemFrontend<I extends ItemId> extends ItemFrontend<I> {
	getBindFlipCard(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.flip-card'),
			'click',
			(element: HTMLElement) => new FlipCardEvent(element.classList.contains('show-question'))
		);
	}

	getKeyBindFlipCard(): KeyBindRecord {
		return new KeyBindRecord(
			(key, answers, type) => key === ' ',
			(answers, type) => new FlipCardEvent(document.querySelector('.flip-card').classList.contains('show-question')),
			true
		);
	}

	hideCardSides(): void {
		[
			document.querySelector('.item .flashcard .question'),
			document.querySelector('.item .flashcard .answer'),
		].forEach(element => {
			element.classList.add('d-none');
		});
	}

	displayCardFront(): void {
		const front = document.querySelector('.item .flashcard .question');
		front.classList.remove('d-none');
	}

	displayCardBack(): void {
		this.displayCardFront();
		const back = document.querySelector('.item .flashcard .answer');
		back.classList.remove('d-none');
	}

	displayItemClass(content: ContentItem<I>): DisplayItemFrontend<I> {
		if (content.type === 'flashcard') {
			return new FlashcardFrontend();
		} else if (content.type === 'colorflashcard') {
			// TODO: return new ColorFlashcardFrontend();
			return new FlashcardFrontend();
		} else {
			return new UnknownFrontend();
		}
	}
}

